<template>
  <v-dialog v-model="opened">
    <v-card class="basic-modal" elevation="8" style="width: 720px">
      <v-card-title class="d-flex justify-space-between">
        <h5 class="heading--text d-flex align-center">
          <span>{{ $t('heading.instance.advanced.modal.phpSettings.title') }}</span>
        </h5>
        <v-btn v-if="!submitting" icon x-small @click="opened = false">
          <v-icon size="24">$close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div>
          <p class="mb-4 p-2 p-3">
            {{ $t('heading.instance.advanced.modal.phpSettings.field') }}
            <strong>{{ instance.domain }}</strong>
          </p>

          <v-form class="d-flex flex-column" ref="form">
            <v-data-table
              :items="phpSettings"
              :headers="headers"
              :editable="true"
              hide-default-footer
              hide-default-header
              disable-pagination
            >
              <template v-slot:header>
                <thead>
                <tr>
                  <th class="text-start" style="width: 250px;">{{ $t('table.header.directive') }}</th>
                  <th class="text-start" style="width: 300px;">{{ $t('table.header.value') }}</th>
                  <th class="text-start" style="width: 80px;"></th>
                </tr>
                </thead>
              </template>

              <template v-slot:item.directive="{ item }">
                <div class="d-flex align-center">
                  <v-text-field
                    v-if="custom && item.editing"
                    class="mb-0"
                    v-model="item.directive"
                    :rules="[(value) => !!value]"
                    outlined
                    dense
                    block
                    small
                    placeholder="Directive"
                    hide-details="auto"
                    type="text"
                    style="min-width: 180px"
                  ></v-text-field>
                  <span
                    v-else
                    class="mr-1"
                  >{{ item.directive }}</span>
                  <form-help-icon
                    v-if="i18next().exists(`tooltip.instance.phpSetting.${item.directive}`) && (!custom || (custom && !item.editing))"
                    :text="$t(`tooltip.instance.phpSetting.${item.directive}`)"
                  />
                </div>
              </template>

              <template v-slot:item.value="{ item, index }">
                <div class="d-flex align-center">
                  <v-select
                    v-if="item.editing && item.type === 'select'"
                    class="mb-0 text-truncate"
                    v-model="item.value"
                    :rules="[(value) => !!value]"
                    :ref="'configName' + index"
                    :items="item.options"
                    outlined
                    dense
                    block
                    small
                    hide-details="auto"
                    style="min-width: 260px; max-width: 300px"
                  />
                  <v-text-field
                    v-else-if="item.editing"
                    class="mb-0"
                    v-model="item.value"
                    :rules="[(value) => !!value]"
                    outlined
                    dense
                    block
                    small
                    placeholder="Value"
                    hide-details="auto"
                    type="text"
                    style="min-width: 180px"
                  ></v-text-field>
                  <span
                    v-else
                    class="ms-1"
                  >{{ item.value }}</span>
                </div>
              </template>

              <template v-slot:item.actions="{ item, index }">
                <v-tooltip
                  v-if="!item.editing"
                  transition="custom-tooltip"
                  open-delay="150"
                  bottom
                  offset-overflow
                  nudge-bottom="4px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      small
                      @click="item.editing = true"
                    >
                      <v-icon size="16" class="gray--text text--darken-1"
                      >$edit2
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('button.edit') }}</span>
                </v-tooltip>
                <v-tooltip
                  v-if="item.editing"
                  transition="custom-tooltip"
                  open-delay="150"
                  bottom
                  offset-overflow
                  nudge-bottom="4px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      small
                      @click="stopEditing(item)"
                    >
                      <v-icon size="16" class="gray--text text--darken-1"
                      >$close
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('button.cancel') }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="custom"
                  transition="custom-tooltip"
                  open-delay="150"
                  bottom
                  offset-overflow
                  nudge-bottom="4px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      small
                      @click="removeRecord(index)"
                    >
                      <v-icon size="16" class="gray--text text--darken-1"
                      >$thrash
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('button.delete') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
            <div
              v-if="custom"
              class="mt-2"
            >
              <v-btn
                elevation="0"
                outlined
                @click="addDirective"
              >
                {{ $t('button.addDirective') }}
              </v-btn>
            </div>
          </v-form>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex flex-wrap pb-4 pt-4">
        <div class="w-100 pb-2">
          <v-btn
            class="primary"
            large
            block
            elevation="0"
            @click="submit()"
            :loading="submitting"
          >
            <span class="p-2 font-weight-600">{{ $t('button.saveChanges') }}</span>
          </v-btn>
        </div>
        <div class="w-100 pb-2">
          <v-btn
            large
            block
            elevation="0"
            @click="opened = false"
            :disabled="submitting"
          >
            <span
              class="p-2"
              :class="'font-weight-light gray--text text--darken-1'"
            >{{ $t('button.cancel') }}
            </span>
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from "@/apis/Api";
import FormHelpIcon from "@/components/FormHelpIcon.vue";
import i18next from "i18next";

export default {
  components: {FormHelpIcon},
  props: {
    open: Boolean,
    instance: Object,
    phpSettings: Array,
    custom: Boolean,
  },
  data() {
    return {
      submitting: false,
      headers: [
        {text: "Directive", value: "directive", sortable: false},
        {text: "Value", value: "value", sortable: false},
        {text: "", value: "actions", sortable: false},
      ],
    };
  },
  computed: {
    opened: {
      get() {
        return this.open;
      },
      set(value) {
        this.$emit("update:open", value);
      },
    },
  },
  methods: {
    i18next() {
      return i18next
    },
    submit() {
      if (!this.$refs.form.validate()) return;

      this.submitting = true;
      Api.put(
        `/server-accounts/${this.instance.hostingId}/domains/${this.instance.domain}/php-settings`,
        this.phpSettings
      )
        .then(() => {
          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.$t('notification.instance.settings.update')
          });

          this.phpSettings.map(item => {
            item.editing = false;
            return item;
          })

          this.opened = false;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    removeRecord(index) {
      this.phpSettings.splice(index, 1);
    },
    stopEditing(item) {
      item.editing = false;
    },
    addDirective() {
      this.phpSettings.push({
        directive: '',
        value: '',
        editing: true,
        type: 'text',
        options: []
      })
    }
  },
};
</script>

<style src="../../../../components/modal/BasicModal.scss" lang="scss" scoped/>
<style lang="scss" scoped>
.data-table .actions-row:last-child {
  justify-content: flex-end;
}

.data-table {
  .actions-row {
    .v-btn {
      color: var(--v-text-darken1) !important;
    }
  }
}
</style>